import React, { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { FeatureCollection, Point } from 'geojson';
import { CargoContractStatusTypes } from '@shared/utils/constants';
import { Port } from '@api/utils/ports';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';

type ClusteredCargoMapLayerProps = {
	cargos?: CargoDetails[];
	showTentativeBookings?: boolean;
	showContractedCargos?: boolean;
	selectedLoadingPortId: number | null;
};

const ClusteredCargoMapLayer = ({
	cargos,
	showTentativeBookings,
	showContractedCargos,
	selectedLoadingPortId,
}: ClusteredCargoMapLayerProps) => {
	const cargoGeoJson = useMemo<FeatureCollection<Point>>(() => {
		const portMap = new Map<
			number,
			{
				port: Port;
				hasContracted: boolean;
				hasTentative: boolean;
				cargoCount: number;
			}
		>();

		(cargos ?? []).forEach((cargo) => {
			const loadingPort = cargo.loadingPorts?.[0];

			if (loadingPort == null) {
				return;
			}

			const isContracted = (
				cargo.contractStatus === CargoContractStatusTypes.Contracted
			);
			const isTentative = (
				cargo.contractStatus === CargoContractStatusTypes.TentativeBooking
			);

			if (!isContracted && !isTentative) {
				return;
			}

			if (isContracted && !showContractedCargos) {
				return;
			}

			if (isTentative && !showTentativeBookings) {
				return;
			}

			const existing = portMap.get(loadingPort.id) ?? {
				port: loadingPort,
				hasContracted: false,
				hasTentative: false,
				cargoCount: 0,
			};

			existing.hasContracted ||= isContracted;
			existing.hasTentative ||= isTentative;
			existing.cargoCount += 1;

			portMap.set(loadingPort.id, existing);
		});

		return {
			type: 'FeatureCollection',
			features: Array.from(portMap.values()).map((entry) => ({
				type: 'Feature',
				properties: {
					portId: entry.port.id,
					portName: entry.port.name,
					cargoCount: entry.cargoCount,
					contractStatus: entry.hasContracted ?
						CargoContractStatusTypes.Contracted :
						CargoContractStatusTypes.TentativeBooking,
					isSelected: selectedLoadingPortId === entry.port.id,
				},
				geometry: {
					type: 'Point',
					coordinates: [
						entry.port.longitude,
						entry.port.latitude,
					],
				},
			})),
		};
	}, [
		cargos,
		showTentativeBookings,
		showContractedCargos,
		selectedLoadingPortId,
	]);

	return (
		<Source
			id="cargo-loading-ports"
			type="geojson"
			data={cargoGeoJson}
			cluster
			clusterMaxZoom={5}
			clusterRadius={45}
		>
			<Layer
				id="cargo-loading-port-clusters"
				type="circle"
				filter={['has', 'point_count']}
				paint={{
					'circle-color': '#ffffff',
					'circle-radius': [
						'step',
						['get', 'point_count'],
						18,
						5,
						22,
						10,
						28,
					],
					'circle-stroke-width': 2,
					'circle-stroke-color': '#1677ff',
				}}
			/>

			<Layer
				id="cargo-loading-port-cluster-count"
				type="symbol"
				filter={['has', 'point_count']}
				layout={{
					'text-field': ['get', 'point_count_abbreviated'],
					'text-size': 12,
					'text-font': ['Open Sans Bold'],
				}}
				paint={{
					'text-color': '#1677ff',
				}}
			/>

			<Layer
				id="cargo-loading-port-points"
				type="circle"
				filter={['!', ['has', 'point_count']]}
				paint={{
					'circle-radius': [
						'case',
						['==', ['get', 'isSelected'], true],
						14,
						10,
					],
					'circle-color': [
						'match',
						['get', 'contractStatus'],
						CargoContractStatusTypes.TentativeBooking,
						'#f4b400',
						CargoContractStatusTypes.Contracted,
						'#8e24aa',
						'#999999',
					],
					'circle-stroke-width': [
						'case',
						['==', ['get', 'isSelected'], true],
						4,
						2,
					],
					'circle-stroke-color': '#ffffff',
				}}
			/>

			<Layer
				id="cargo-loading-port-point-count"
				type="symbol"
				filter={['!', ['has', 'point_count']]}
				layout={{
					'text-field': ['to-string', ['get', 'cargoCount']],
					'text-size': 11,
					'text-font': ['Open Sans Bold'],
					'text-allow-overlap': true,
				}}
				paint={{
					'text-color': '#ffffff',
				}}
			/>
		</Source>
	);
};

export default ClusteredCargoMapLayer;